<template>
  <div class="booking-page">
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      bottom
      right
      app
      class="global-snackbar"
      style="z-index: 100000 !important"
      :z-index="100000"
    >
      {{ snackbar.message }}

      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <v-container fluid class="booking-container pa-4 pa-md-6">
      <!-- Page Header -->
      <div class="page-header mb-4">
        <div class="page-header-row">
          <v-btn icon @click="$router.go(-1)" class="back-btn">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <div>
            <h1 class="page-title">Schedule & Confirm Booking</h1>
            <p class="service-name">{{ serviceName }}</p>
          </div>
        </div>
      </div>

      <v-row>
        <!-- Left Section - Booking Details -->
        <v-col cols="12" md="8">
          <v-card class="booking-card elevation-1">
            <div class="booking-top-layout">
            <!-- Step 1: Time & Date Selection -->
            <div class="booking-section booking-section-simple booking-time-card">
              <div class="simple-section-header">
                <h2 class="simple-section-title">Time & Date For Service</h2>
                <p class="simple-section-description">
                  Choose a day and time for the service your booking
                </p>
              </div>

              <div class="datetime-compact">
                <div class="compact-group">
                  <label class="compact-label">Date & Time</label>
                  <div class="compact-buttons">
                    <v-btn
                      small
                      :outlined="selectedDate !== 'Today'"
                      :color="selectedDate === 'Today' ? '#f46523' : 'grey'"
                      :dark="selectedDate === 'Today'"
                      :class="{ 'active-compact': selectedDate === 'Today' }"
                      class="compact-btn"
                      @click="selectedDate = 'Today'"
                    >
                      Today
                    </v-btn>
                    <v-btn
                      small
                      :outlined="selectedDate !== 'Tomorrow'"
                      :color="selectedDate === 'Tomorrow' ? '#f46523' : 'grey'"
                      :dark="selectedDate === 'Tomorrow'"
                      :class="{ 'active-compact': selectedDate === 'Tomorrow' }"
                      class="compact-btn"
                      @click="selectedDate = 'Tomorrow'"
                    >
                      Tomorrow
                    </v-btn>
                    <v-menu v-model="customDateMenu" offset-y>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          small
                          :outlined="selectedDate !== 'Other'"
                          :color="selectedDate === 'Other' ? '#f46523' : 'grey'"
                          :dark="selectedDate === 'Other'"
                          v-bind="attrs"
                          v-on="on"
                          :class="{ 'active-compact': selectedDate === 'Other' }"
                          class="compact-btn"
                        >
                          <v-icon x-small class="mr-1">mdi-calendar</v-icon>
                          {{ customDate || "Other" }}
                        </v-btn>
                      </template>
                      <v-date-picker
                        v-model="customDate"
                        color="#f46523"
                        :min="new Date().toISOString().substr(0, 10)"
                        @input="handleCustomDateSelect"
                      />
                    </v-menu>

                    <v-btn
                      small
                      outlined
                      color="#f46523"
                      class="compact-btn time-select-btn-inline"
                      @click="timeSheet = true"
                    >
                      <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                      {{ selectedTime || "Select Time" }}
                    </v-btn>
                  </div>

                  <v-dialog v-model="timeSheet" max-width="400">
                    <v-card class="time-sheet-card">
                      <v-card-title class="sheet-header">
                        <span class="sheet-title">Select Time</span>
                        <v-spacer></v-spacer>
                        <v-btn icon @click="timeSheet = false">
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                      </v-card-title>
                      <v-card-text class="sheet-content">
                        <div class="time-list-grid">
                          <div
                            v-for="time in generatedTimeSlots"
                            :key="time"
                            class="time-slot-btn"
                            :class="{
                              selected: selectedTime === time,
                              disabled: isTimeDisabled(time),
                            }"
                            @click="selectTime(time)"
                          >
                            {{ time }}
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-dialog>
                </div>
              </div>

              <!-- Selection Summary for Date & Time -->
              <div
                v-if="selectedDate || selectedTime"
                class="selection-summary-pill mt-4 time-selection-summary"
              >
                <div class="summary-icon">
                  <v-icon color="white" x-small>mdi-calendar-check</v-icon>
                </div>
                <span class="summary-text">
                  Selected:
                  <strong>
                    {{
                      selectedDate
                        ? selectedDate === "Today"
                          ? "Today"
                          : selectedDate === "Tomorrow"
                          ? "Tomorrow"
                          : customDate
                        : "Date not selected"
                    }}
                  </strong>
                  <span class="summary-separator">|</span>
                  <strong>{{ selectedTime || "Time not selected" }}</strong>
                </span>
              </div>
            </div>

            <!-- Step 2: Service Address - Simplified Modal Setup -->
            <div class="booking-section booking-section-simple booking-address-card">
              <div class="simple-section-header">
                <h2 class="simple-section-title">Service Address</h2>
                <p class="simple-section-description">
                  Select or add the address for this booking
                </p>
              </div>

              <!-- Loading State -->
              <div v-if="loading" class="loading-state">
                <v-progress-circular indeterminate color="#f46523" size="32" />
                <p class="loading-text">Loading your addresses...</p>
              </div>

              <div v-else-if="addresses.length > 0" class="address-simple-block">
                <div v-if="selectedAddress" class="address-selected-simple">
                  <div class="address-selected-simple__header">
                    <div class="address-selected-simple__title-wrap">
                      <div
                        class="address-type-icon"
                        :style="{
                          backgroundColor: getAddressTypeColor(selectedAddress.title),
                        }"
                      >
                        <v-icon x-small color="white">
                          {{ getAddressTypeIcon(selectedAddress.title) }}
                        </v-icon>
                      </div>
                      <div>
                        <div class="address-selected-simple__title">
                          {{ selectedAddress.name || "Saved Address" }}
                        </div>
                        <div class="address-selected-simple__phone">
                          {{ formatPhone(selectedAddress.contact_number) }}
                        </div>
                      </div>
                    </div>
                    <v-icon color="#4caf50">mdi-check-circle</v-icon>
                  </div>

                  <div class="address-selected-simple__line">
                    {{ selectedAddress.address_line_1 }}
                  </div>
                  <div class="address-selected-simple__meta">
                    <span v-if="selectedAddress.landmark">
                      {{ selectedAddress.landmark }}
                    </span>
                    <span v-if="selectedAddress.landmark && selectedAddress.city">
                      ,
                    </span>
                    <span>{{ selectedAddress.city }}</span>
                  </div>
                </div>

                <div class="address-simple-actions">
                  <v-btn
                    outlined
                    color="#f46523"
                    class="address-simple-btn"
                    @click="openAddressList"
                  >
                    Change Address
                  </v-btn>
                  <v-btn
                    outlined
                    color="#f46523"
                    class="address-simple-btn"
                    @click="openAddAddressForm"
                  >
                    Add New Address
                  </v-btn>
                </div>
              </div>

              <!-- No Address State -->
              <div v-else class="no-address-state-compact">
                <div class="no-address-content">
                  <v-icon size="48" color="#e0e0e0" class="mb-2"
                    >mdi-home-outline</v-icon
                  >
                  <h3 class="no-address-title">No saved addresses</h3>
                  <p class="no-address-text">
                    Add a service address to continue booking
                  </p>
                  <v-btn
                    color="#f46523"
                    dark
                    depressed
                    class="mt-3 white--text add-address-primary-btn"
                    @click="openAddAddressForm"
                    small
                  >
                    <v-icon left small>mdi-plus</v-icon>
                    Add Address
                  </v-btn>
                </div>
              </div>

              <!-- Selection Summary for Address -->
              <div v-if="selectedAddress" class="selection-summary-pill mt-4">
                <div class="summary-icon">
                  <v-icon color="white" x-small>mdi-map-marker-check</v-icon>
                </div>
                <span class="summary-text">
                  Service at <strong>{{ selectedAddress.title }}</strong
                  >: {{ selectedAddress.address_line_1 }}
                </span>
              </div>
            </div>
            </div>

            <v-divider class="section-divider" />

            <!-- Step 3: Special Instructions -->
            <div class="booking-section booking-section-simple">
              <div class="simple-section-header">
                <h2 class="simple-section-title">Special Instructions</h2>
                <p class="simple-section-description">
                  Add any specific notes for the service
                </p>
              </div>

              <v-textarea
                v-model="bookingDescription"
                outlined
                dense
                placeholder="e.g. Please bring extra cleaning supplies, Call before arriving, etc."
                color="#f46523"
                rows="3"
                hide-details
                class="mt-3 special-instructions-field"
              ></v-textarea>
            </div>
          </v-card>
        </v-col>

        <!-- Right Section - Payment Summary -->
        <v-col cols="12" md="4">
          <div class="payment-sticky">
            <!-- Payment Summary -->
            <v-card class="payment-card elevation-2">
              <div class="payment-header">
                <h2 class="payment-title">Payment Summary</h2>
              </div>

              <v-divider class="my-3" />

              <div class="payment-content">
                <div class="payment-row">
                  <span class="payment-label">Service Amount</span>
                  <span class="payment-value">₹{{ payment.totalAmount }}</span>
                </div>

                <div
                  class="payment-row discount-row"
                  v-if="payment.membershipOffer > 0"
                >
                  <span class="payment-label">
                    <v-icon x-small color="success" class="mr-1"
                      >mdi-tag</v-icon
                    >
                    Membership Discount
                  </span>
                  <span class="payment-value discount"
                    >-₹{{ payment.membershipOffer }}</span
                  >
                </div>

                <div
                  class="payment-row discount-row"
                  v-if="payment.couponDiscount > 0"
                >
                  <span class="payment-label">
                    <v-icon x-small color="success" class="mr-1"
                      >mdi-ticket-percent</v-icon
                    >
                    Coupon Discount
                  </span>
                  <span class="payment-value discount"
                    >-₹{{ payment.couponDiscount }}</span
                  >
                </div>

                <div class="payment-row" v-if="payment.platformFee > 0">
                  <span class="payment-label">Platform Fee</span>
                  <span class="payment-value">₹{{ payment.platformFee }}</span>
                </div>

                <v-divider class="my-4" />

                <div class="payment-total">
                  <span class="total-label">Total Payable</span>
                  <span class="total-amount">₹{{ totalPayment }}</span>
                </div>

                <!-- Cancellation Policy -->
                <div class="policy-box">
                  <div class="policy-header">
                    <v-icon small color="#f46523" class="mr-1"
                      >mdi-information</v-icon
                    >
                    <span class="policy-title">Cancellation Policy</span>
                  </div>
                  <p class="policy-text">
                    100% refund if cancelled within 1 hour of booking. No refund
                    after 1 hour.
                  </p>
                </div>

                <!-- Confirm Button -->
                <v-btn
                  block
                  x-large
                  :color="isConfirmDisabled ? '#f5f5f5' : '#f46523'"
                  :dark="!isConfirmDisabled"
                  depressed
                  class="confirm-btn"
                  @click="confirmBooking"
                  :disabled="isConfirmDisabled"
                  :loading="confirmLoading"
                >
                  <v-icon left>mdi-check-circle</v-icon>
                  Confirm Booking
                </v-btn>

                <div v-if="isConfirmDisabled" class="missing-steps-hint mt-3">
                  <v-icon x-small color="#999" class="mr-1"
                    >mdi-information-outline</v-icon
                  >
                  <span>Complete all steps to confirm your booking</span>
                </div>

                <p class="booking-note">
                  By confirming, you agree to our terms and conditions
                </p>
              </div>
            </v-card>
          </div>
        </v-col>
      </v-row>

      <!-- Booking Success Dialog -->
      <v-dialog v-model="addressListExpanded" max-width="640" scrollable>
        <v-card class="address-modal-card">
          <div class="address-modal-header">
            <div>
              <h3 class="address-modal-title">Select Service Address</h3>
              <p class="address-modal-subtitle">
                Choose the address where you want the service.
              </p>
            </div>
            <v-btn icon @click="addressListExpanded = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <div class="address-modal-body">
            <v-radio-group
              v-model="selectedAddressId"
              @change="selectAddressApi"
              class="address-list"
              color="#f46523"
            >
              <div
                v-for="address in addresses"
                :key="address._id"
                class="address-card"
                :class="{
                  'address-selected-card': selectedAddressId === address._id,
                }"
              >
                <div
                  v-if="!showDeleteConfirmation[address._id]"
                  class="address-card-content"
                >
                  <div class="address-header">
                    <div class="address-type-wrapper">
                      <div
                        class="address-type-icon"
                        :style="{
                          backgroundColor: getAddressTypeColor(address.title),
                        }"
                      >
                        <v-icon x-small color="white">
                          {{ getAddressTypeIcon(address.title) }}
                        </v-icon>
                      </div>
                      <div class="address-info-compact">
                        <span class="address-type-text">{{ address.title }}</span>
                        <span class="address-contact-compact">
                          {{ address.name }} •
                          {{ formatPhone(address.contact_number) }}
                        </span>
                      </div>
                    </div>
                    <v-radio
                      :value="address._id"
                      class="address-radio"
                      color="#f46523"
                      hide-details
                      @click="selectedAddressId = address._id"
                    />
                  </div>

                  <div class="address-details-compact">
                    <div class="address-location-line">
                      <v-icon x-small class="mr-1" color="#666">
                        mdi-map-marker
                      </v-icon>
                      <span class="address-text-compact">
                        {{ address.address_line_1 }}
                      </span>
                    </div>
                    <div class="address-meta-line">
                      <span v-if="address.landmark" class="address-landmark">
                        • {{ address.landmark }}
                      </span>
                      <span class="address-city">• {{ address.city }}</span>
                    </div>
                  </div>

                  <div class="address-actions-compact">
                    <v-btn
                      x-small
                      dark
                      depressed
                      color="#f46523"
                      class="action-btn-compact edit-btn"
                      @click.stop="openEditAddressDialog(address)"
                    >
                      <v-icon x-small color="white">
                        mdi-pencil-outline
                      </v-icon>
                      <span class="action-text">Edit</span>
                    </v-btn>
                    <v-btn
                      x-small
                      dark
                      depressed
                      color="#f46523"
                      class="action-btn-compact delete-btn"
                      @click.stop="showDeleteConfirmationSection(address)"
                    >
                      <v-icon x-small color="white">
                        mdi-delete-outline
                      </v-icon>
                      <span class="action-text">Delete</span>
                    </v-btn>
                  </div>
                </div>

                <div v-else class="delete-confirmation-section">
                  <div class="delete-header">
                    <v-icon color="error" small class="mr-2">
                      mdi-alert-circle-outline
                    </v-icon>
                    <span class="delete-title">Delete Address?</span>
                  </div>
                  <p class="delete-message">
                    Are you sure you want to delete this address? This action
                    cannot be undone.
                  </p>
                  <div class="delete-actions">
                    <v-btn
                      text
                      small
                      color="#666"
                      class="cancel-delete-btn"
                      @click="cancelDelete(address)"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="error"
                      dark
                      depressed
                      small
                      class="confirm-delete-btn"
                      @click="deleteAddress(address)"
                      :loading="deletingAddressId === address._id"
                    >
                      <v-icon left small>mdi-delete</v-icon>
                      Delete
                    </v-btn>
                  </div>
                </div>
              </div>
            </v-radio-group>
          </div>

          <div class="address-modal-footer">
            <v-btn color="#f46523" dark depressed @click="openAddAddressForm">
              <v-icon left small>mdi-plus</v-icon>
              Add New Address
            </v-btn>
          </div>
        </v-card>
      </v-dialog>

      <v-dialog v-model="addressFormExpanded" max-width="1080" scrollable>
        <v-card class="address-form-modal">
          <div class="address-modal-header">
            <div>
              <h3 class="address-modal-title">
                {{ isEditMode ? "Edit Address" : "Add New Address" }}
              </h3>
              <p class="address-modal-subtitle">
                Fill in the details and pin the location on the map.
              </p>
            </div>
            <v-btn icon @click="collapseAddressForm">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <div ref="addressFormSectionRef" class="panel-content address-panel-content">
            <v-form ref="addressForm" v-model="addressFormValid">
              <div class="address-form-layout">
                <div class="address-form-fields">
                  <div class="address-location-tools">
                    <div class="map-search-row">
                      <v-text-field
                        v-model="locationSearchQuery"
                        label="Search location"
                        outlined
                        dense
                        hide-details="auto"
                        class="form-field-compact map-search-input"
                        @keyup.enter="searchLocations"
                      />
                      <v-btn
                        small
                        depressed
                        color="#f46523"
                        class="map-search-btn white--text"
                        :loading="searchingLocations"
                        @click="searchLocations"
                      >
                        Search
                      </v-btn>
                    </div>

                    <div class="map-picker-actions">
                      <v-btn
                        small
                        text
                        color="#f46523"
                        class="detect-location-btn"
                        @click="detectCurrentLocation"
                        :loading="detectingLocation"
                      >
                        <v-icon left small>mdi-crosshairs-gps</v-icon>
                        Use Current Location
                      </v-btn>
                      <a
                        v-if="mapExternalUrl"
                        :href="mapExternalUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="map-open-link"
                      >
                        Open Google Maps
                      </a>
                    </div>

                    <div
                      v-if="locationHelpMessage"
                      class="location-help-message"
                      :class="{
                        'location-help-message-error':
                          locationHelpTone === 'error',
                      }"
                    >
                      <v-icon x-small class="mr-1">
                        {{
                          locationHelpTone === "error"
                            ? "mdi-information-outline"
                            : "mdi-check-circle-outline"
                        }}
                      </v-icon>
                      <span>{{ locationHelpMessage }}</span>
                    </div>

                    <div
                      v-if="locationSearchResults.length"
                      class="map-search-results"
                    >
                      <button
                        v-for="result in locationSearchResults"
                        :key="result.place_id"
                        type="button"
                        class="map-result-item"
                        @click="applyLocationResult(result)"
                      >
                        <span class="map-result-name">
                          {{ result.display_name }}
                        </span>
                        <span class="map-result-coords">
                          {{ Number(result.lat).toFixed(5) }},
                          {{ Number(result.lon).toFixed(5) }}
                        </span>
                      </button>
                    </div>
                  </div>

                  <v-row dense>
                    <v-col cols="12">
                      <v-text-field
                        v-model="addressForm.name"
                        label="Name *"
                        outlined
                        dense
                        :rules="[(v) => !!v || 'Name is required']"
                        hide-details="auto"
                        class="form-field-compact"
                      />
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="addressForm.address_line_1"
                        label="House/Flat Number *"
                        outlined
                        dense
                        :rules="[(v) => !!v || 'Address is required']"
                        hide-details="auto"
                        class="form-field-compact"
                      />
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="addressForm.landmark"
                        label="Landmark *"
                        outlined
                        dense
                        :rules="[(v) => !!v || 'Landmark is required']"
                        hide-details="auto"
                        class="form-field-compact"
                      />
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="addressForm.contact_number"
                        label="Contact Number *"
                        outlined
                        dense
                        type="tel"
                        maxlength="10"
                        :rules="[
                          (v) => !!v || 'Contact number is required',
                          (v) => /^[0-9]{10}$/.test(v) || 'Must be 10 digits',
                        ]"
                        hide-details="auto"
                        class="form-field-compact"
                      />
                    </v-col>
                    <v-col cols="12" class="city-field-wrap">
                      <v-text-field
                        v-model="addressForm.city"
                        label="City *"
                        outlined
                        dense
                        :rules="[(v) => !!v || 'City is required']"
                        hide-details="auto"
                        class="form-field-compact"
                      />
                    </v-col>
                    <v-col cols="12">
                      <div class="address-type-selector-compact">
                        <div class="address-type-options">
                          <div
                            v-for="type in ['Home', 'Work', 'Other']"
                            :key="type"
                            class="address-type-option-compact"
                            :class="{
                              'address-type-selected': addressForm.title === type,
                            }"
                            @click="addressForm.title = type"
                          >
                            <div
                              class="type-option-icon-compact"
                              :style="{
                                backgroundColor: getAddressTypeColor(type),
                              }"
                            >
                              <v-icon x-small color="white">
                                {{ getAddressTypeIcon(type) }}
                              </v-icon>
                            </div>
                            <span class="type-option-text">{{ type }}</span>
                          </div>
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="12">
                      <v-checkbox
                        v-model="addressForm.is_selected"
                        label="Set as default address"
                        hide-details
                        class="mt-0 default-address-checkbox"
                        color="#f46523"
                        dense
                      />
                    </v-col>
                  </v-row>
                </div>

                <div class="address-map-pane">
                  <div class="map-pane-toolbar">
                    <div class="map-pane-title">Choose on map</div>
                    <div class="map-pane-note">
                      Tap or drag the pin to set the exact location
                    </div>
                  </div>
                  <div
                    ref="addressMap"
                    class="address-map-frame"
                    :class="{ 'address-map-loading': !leafletReady }"
                  ></div>
                  <div v-if="!leafletReady" class="map-preview-empty">
                    <v-icon color="#f46523" class="mr-2">
                      mdi-map-search-outline
                    </v-icon>
                    <span>Loading map...</span>
                  </div>
                  <div
                    v-if="
                      Number.isFinite(Number(addressForm.latitude)) &&
                      Number.isFinite(Number(addressForm.longitude))
                    "
                    class="map-coordinates"
                  >
                    <v-icon x-small color="#f46523">mdi-crosshairs-gps</v-icon>
                    <span>
                      {{ Number(addressForm.latitude).toFixed(5) }},
                      {{ Number(addressForm.longitude).toFixed(5) }}
                    </span>
                  </div>
                </div>
              </div>
            </v-form>
          </div>

          <div class="panel-footer-compact">
            <v-btn text @click="collapseAddressForm" class="cancel-btn" small>
              Cancel
            </v-btn>
            <v-btn
              small
              depressed
              class="save-btn"
              :style="isSaveDisabled ? disabledBtnStyle : enabledBtnStyle"
              :dark="!isSaveDisabled"
              @click="saveAddress"
              :loading="savingAddress"
              :disabled="isSaveDisabled"
            >
              {{ isEditMode ? "Update" : "Save" }}
            </v-btn>
          </div>
        </v-card>
      </v-dialog>

      <v-dialog v-model="bookingSuccessDialog" max-width="560" persistent>
        <v-card class="success-dialog-modern">
          <div class="success-header-graphic">
            <div class="success-icon-wrapper">
              <v-icon size="60" color="white">mdi-check-bold</v-icon>
            </div>
            <div class="confetti-particles"></div>
          </div>

          <v-card-text class="text-center success-body">
            <h2 class="success-title">Success!</h2>
            <h3 class="success-booking-id">
              Booking #{{ Math.floor(Math.random() * 9000) + 1000 }} confirmed
            </h3>

            <p class="success-subtitle mt-3">
              Your service request has been received. Our partner will arrive at
              the scheduled time.
            </p>

            <v-divider class="my-4" />

            <div class="booking-details-summary">
              <div class="detail-item">
                <v-icon small color="#666" class="mr-2">mdi-calendar</v-icon>
                <span class="detail-label">Date:</span>
                <span class="detail-value">{{
                  selectedDate === "Other" ? customDate : selectedDate
                }}</span>
              </div>
              <div class="detail-item mt-2">
                <v-icon small color="#666" class="mr-2"
                  >mdi-clock-outline</v-icon
                >
                <span class="detail-label">Time:</span>
                <span class="detail-value">{{ selectedTime }}</span>
              </div>
            </div>

            <div class="success-app-links">
              <div class="success-app-title">Download our app</div>
              <div class="success-app-buttons">
                <a
                  href="https://apps.apple.com/in/app/oh-yes-home-solution/id6471665769"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="success-store-btn app-store-btn"
                >
                  <v-icon color="#111" class="mr-2">mdi-apple</v-icon>
                  <span>App Store</span>
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.ohyes.app&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="success-store-btn play-store-btn"
                >
                  <v-icon color="#2e7d32" class="mr-2">mdi-google-play</v-icon>
                  <span>Google Play</span>
                </a>
              </div>
            </div>
          </v-card-text>

          <v-card-actions class="justify-center flex-column success-actions">
            <v-btn
              block
              color="#f46523"
              dark
              x-large
              depressed
              class="success-action-btn mb-3"
              @click="goToBookings"
            >
              Go to Bookings
            </v-btn>
            <v-btn
              text
              color="grey darken-1"
              class="close-dialog-btn"
              @click="goToHome"
            >
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import {
  getAllAddresses,
  selectAddress,
  addAddress,
  editAddress,
  deleteAddress,
} from "../services/address.service";
import { addToCart, createBooking } from "../services/booking.service";
import gsap from "gsap";
import { getApiBaseUrl } from "../config/api";

export default {
  name: "AddBookings",

  data() {
    return {
      selectedDate: "Today",
      customDate: null,
      customDateMenu: false,
      disabledBtnStyle: {
        backgroundColor: "#e0e0e0",
        color: "#9e9e9e",
        boxShadow: "none",
      },

      enabledBtnStyle: {
        backgroundColor: "#f46523",
        color: "#ffffff",
      },

      selectedAddressId: null,
      selectedServiceTypeId: null,
      serviceName: "Service",
      bookingSuccessDialog: false,

      confirmLoading: false,
      bookingDescription: "",

      snackbar: {
        show: false,
        message: "",
        color: "success",
      },

      addresses: [],
      selectedAddress: null,
      loading: false,
      addressListExpanded: false,
      preferredAddressApplied: false,

      addressFormExpanded: false,
      isEditMode: false,
      savingAddress: false,
      tempSelectedAddressId: null,
      editingAddressId: null,

      addressFormValid: false,
      addressForm: {
        name: "",
        title: "Other",
        landmark: "",
        address_line_1: "",
        city: "",
        latitude: null,
        longitude: null,
        contact_number: "",
        is_selected: false,
      },

      // Delete confirmation state
      showDeleteConfirmation: {},
      deletingAddressId: null,
      detectingLocation: false,
      locationHelpMessage: "",
      locationHelpTone: "info",
      searchingLocations: false,
      locationSearchQuery: "",
      locationSearchResults: [],
      leafletReady: false,
      leafletLoadPromise: null,
      addressMap: null,
      addressMapMarker: null,

      payment: {
        totalAmount: 300,
        membershipOffer: 0,
        couponDiscount: 0,
        platformFee: 0,
      },

      selectedTime: null,
      timeSheet: false,
      generatedTimeSlots: [],
    };
  },

  computed: {
    isSaveDisabled() {
      return !this.addressFormValid || this.savingAddress;
    },
    totalPayment() {
      return (
        this.payment.totalAmount -
        this.payment.membershipOffer -
        this.payment.couponDiscount +
        this.payment.platformFee
      );
    },

    isStep1Completed() {
      return !!(this.selectedDate && this.selectedTime);
    },

    isStep2Completed() {
      return this.isStep1Completed && !!this.selectedAddress;
    },

    isConfirmDisabled() {
      return !this.isStep2Completed;
    },
    mapPreviewUrl() {
      const latitude = Number(this.addressForm.latitude);
      const longitude = Number(this.addressForm.longitude);

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return "";
      }

      return `https://maps.google.com/maps?q=${latitude},${longitude}&z=16&output=embed`;
    },
    mapExternalUrl() {
      const latitude = Number(this.addressForm.latitude);
      const longitude = Number(this.addressForm.longitude);

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return "";
      }

      return `https://www.google.com/maps?q=${latitude},${longitude}`;
    },
  },

  created() {
    let payload = this.$route.params;

    if (!payload?.serviceTypeId) {
      payload = JSON.parse(sessionStorage.getItem("bookingPayload") || "null");
    }

    if (!payload?.serviceTypeId) {
      this.$router.replace("/services");
      return;
    }

    this.selectedServiceTypeId = payload.serviceTypeId;
    this.serviceName = payload.serviceName || "Service";
    this.addressListExpanded = false;

    this.fetchAddresses();
    this.fetchServiceSelection();
    this.generateTimeSlots();
  },

  beforeDestroy() {
    if (this.addressMap) {
      this.addressMap.remove();
      this.addressMap = null;
      this.addressMapMarker = null;
    }
  },

  watch: {
    addressFormExpanded(value) {
      if (!value) return;

      this.$nextTick(async () => {
        await this.ensureAddressMapReady();
        this.syncAddressMapPosition();
      });
    },
    "addressForm.latitude"() {
      this.syncAddressMapPosition();
    },
    "addressForm.longitude"() {
      this.syncAddressMapPosition();
    },
  },

  methods: {
    getTimeZoneParts(date = new Date()) {
      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Kolkata",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).formatToParts(date);
      const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
      return {
        year: Number(map.year),
        month: Number(map.month),
        day: Number(map.day),
        hour: Number(map.hour),
        minute: Number(map.minute),
      };
    },
    formatDateFromParts(parts) {
      const mm = String(parts.month).padStart(2, "0");
      const dd = String(parts.day).padStart(2, "0");
      return `${parts.year}-${mm}-${dd}`;
    },
    buildAddressFromParts(addr = {}, fallback = "") {
      const line = [
        addr.house_number,
        addr.road,
        addr.suburb,
        addr.neighbourhood,
        addr.hamlet,
      ]
        .filter(Boolean)
        .join(", ");

      return line || fallback;
    },
    getCityFromAddress(addr = {}) {
      return (
        addr.city ||
        addr.town ||
        addr.village ||
        addr.county ||
        addr.state_district ||
        ""
      );
    },
    getLandmarkFromAddress(addr = {}) {
      return (
        addr.suburb ||
        addr.neighbourhood ||
        addr.hamlet ||
        addr.quarter ||
        addr.city_district ||
        ""
      );
    },
    addMinutesToTime(timeStr, minutesToAdd) {
      if (!timeStr) return timeStr;
      const [time, period] = timeStr.split(" ");
      let [hours, minutes] = time.split(":").map((v) => parseInt(v, 10));
      if (period === "PM" && hours !== 12) hours += 12;
      if (period === "AM" && hours === 12) hours = 0;

      let total = hours * 60 + minutes + minutesToAdd;
      total = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
      const newHours24 = Math.floor(total / 60);
      const newMinutes = total % 60;
      const newPeriod = newHours24 >= 12 ? "PM" : "AM";
      const newHours12 = newHours24 % 12 || 12;
      return `${newHours12}:${String(newMinutes).padStart(
        2,
        "0"
      )} ${newPeriod}`;
    },
    formatSelectedTime24h(timeStr) {
      if (!timeStr) return "";
      const [time, period] = timeStr.split(" ");
      let [hours, minutes] = time.split(":").map((v) => parseInt(v, 10));
      if (period === "PM" && hours !== 12) hours += 12;
      if (period === "AM" && hours === 12) hours = 0;
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
        2,
        "0"
      )}`;
    },
    handleCustomDateSelect(value) {
      this.customDate = value;
      this.selectedDate = "Other";
      this.customDateMenu = false;
    },
    generateTimeSlots() {
      const slots = [];
      const startHour = 8; // 8 AM
      const endHour = 20; // 8 PM

      for (let h = startHour; h <= endHour; h++) {
        for (let m = 0; m < 60; m += 30) {
          const period = h >= 12 ? "PM" : "AM";
          const hour12 = h % 12 || 12;
          const minuteStr = m === 0 ? "00" : m;
          slots.push(`${hour12}:${minuteStr} ${period}`);
        }
      }
      this.generatedTimeSlots = slots;
    },

    isTimeDisabled(timeStr) {
      if (this.selectedDate !== "Today") return false;

      const nowParts = this.getTimeZoneParts();
      const currentHour = nowParts.hour;
      const currentMinute = nowParts.minute;

      // Parse time string "09:30 AM"
      const [time, period] = timeStr.split(" ");
      let [hours, minutes] = time.split(":");
      hours = parseInt(hours);
      minutes = parseInt(minutes);

      if (period === "PM" && hours !== 12) hours += 12;
      if (period === "AM" && hours === 12) hours = 0;

      if (hours < currentHour) return true;
      if (hours === currentHour && minutes <= currentMinute) return true;

      return false;
    },

    selectTime(time) {
      if (this.isTimeDisabled(time)) return;
      this.selectedTime = time;
      this.timeSheet = false;
    },

    showSnackbar(message, color = "success") {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },

    // Helper methods for address types
    getAddressTypeColor(type) {
      const colors = {
        Home: "#f46523", // Your brand color for Home
        Work: "#2196F3", // Blue for Work
        Other: "#9C27B0", // Purple for Other
      };
      return colors[type] || "#666";
    },

    getAddressTypeIcon(type) {
      const icons = {
        Home: "mdi-home",
        Work: "mdi-briefcase",
        Other: "mdi-map-marker",
      };
      return icons[type] || "mdi-map-marker";
    },

    formatPhone(phone) {
      if (!phone) return "";
      return String(phone);
    },

    // Show delete confirmation section
    showDeleteConfirmationSection(address) {
      this.$set(this.showDeleteConfirmation, address._id, true);
    },

    // Cancel delete
    cancelDelete(address) {
      this.$set(this.showDeleteConfirmation, address._id, false);
    },

    // Delete address
    async deleteAddress(address) {
      if (!address || !address._id) return;

      this.deletingAddressId = address._id;

      try {
        await deleteAddress(address._id);

        // Close delete confirmation
        this.$set(this.showDeleteConfirmation, address._id, false);

        // Refresh addresses
        await this.fetchAddresses();

        // If deleted address was selected, select the first available address
        if (
          this.selectedAddressId === address._id &&
          this.addresses.length > 0
        ) {
          this.selectedAddressId = this.addresses[0]._id;
          await this.selectAddressApi(this.selectedAddressId);
        }
      } catch (error) {
        console.error("Delete error:", error);
        // Show error message
      } finally {
        this.deletingAddressId = null;
      }
    },

    async fetchServiceSelection() {
      if (!this.selectedServiceTypeId) return;

      const token = localStorage.getItem("access_token");

      try {
        const res = await fetch(
          `${getApiBaseUrl()}/services-selection/${this.selectedServiceTypeId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const data = await res.json();

        if (data?.data?.payment_info?.length) {
          const paymentMap = Object.fromEntries(
            data.data.payment_info.map((i) => [i.title, i.value])
          );

          this.payment.totalAmount = Number(paymentMap.total_amount) || 0;
          this.payment.membershipOffer = Number(paymentMap.discount) || 0;
          this.payment.platformFee =
            paymentMap.tax === "N/A" ? 0 : Number(paymentMap.tax) || 0;
        }
      } catch (err) {
        console.error("Service selection API error:", err);
      }
    },

    async fetchAddresses() {
      this.loading = true;
      try {
        const response = await getAllAddresses();
        this.addresses = response.data?.data || [];

        const selectedAddr = this.addresses.find((a) => a.is_selected);
        const otherAddr = this.addresses.find(
          (a) => String(a.title || "").toLowerCase() === "other"
        );
        const defaultAddr =
          !this.preferredAddressApplied && otherAddr
            ? otherAddr
            : selectedAddr || this.addresses[0];

        this.selectedAddress = defaultAddr || null;
        this.selectedAddressId = defaultAddr?._id || null;

        if (
          defaultAddr?._id &&
          selectedAddr?._id &&
          defaultAddr._id !== selectedAddr._id
        ) {
          await selectAddress(defaultAddr._id);
        }

        this.preferredAddressApplied = true;
      } catch (error) {
        console.error("Error fetching addresses:", error);
      } finally {
        this.loading = false;
      }
    },

    async selectAddressApi(addressId) {
      if (!addressId) return;

      try {
        await selectAddress(addressId);

        // refresh addresses
        await this.fetchAddresses();

        // refresh payment/service metadata for new address
        await this.fetchServiceSelection();

        this.addressListExpanded = false;
      } catch (error) {
        console.error("Error selecting address:", error);
      }
    },

    toggleAddressForm() {
      if (this.addressFormExpanded) {
        this.collapseAddressForm();
      } else {
        this.openAddAddressForm();
      }
    },
    toggleAddressList() {
      this.addressListExpanded = !this.addressListExpanded;
    },

    openAddressList() {
      this.addressListExpanded = true;
    },

    openAddAddressForm() {
      this.resetAddressForm();
      this.seedAddressCoordinates();
      this.isEditMode = false;
      this.editingAddressId = null;
      this.addressListExpanded = false;
      this.addressFormExpanded = true;
      this.$nextTick(() => {
        if (this.$refs.addressForm) {
          this.$refs.addressForm.resetValidation();
        }
        this.scrollToAddressForm();
        this.ensureAddressMapReady();
      });
    },

    openEditAddressDialog(address) {
      if (!address || !address._id) {
        console.error("Invalid address object", address);
        return;
      }

      this.editingAddressId = address._id;
      this.isEditMode = true;
      this.addressForm = {
        name: address.name || "",
        title: address.title || "Other",
        landmark: address.landmark || "",
        address_line_1: address.address_line_1 || "",
        city: address.city || "",
        latitude: address.latitude || null,
        longitude: address.longitude || null,
        contact_number: address.contact_number || "",
        is_selected: address.is_selected || false,
      };
      if (
        !Number.isFinite(Number(this.addressForm.latitude)) ||
        !Number.isFinite(Number(this.addressForm.longitude))
      ) {
        this.seedAddressCoordinates();
      }
      this.locationSearchQuery = address.address_line_1 || "";
      this.locationSearchResults = [];
      this.addressListExpanded = false;
      this.addressFormExpanded = true;
      this.$nextTick(() => {
        this.scrollToAddressForm();
        this.ensureAddressMapReady();
      });
    },

    collapseAddressForm() {
      this.addressFormExpanded = false;
    },

    scrollToAddressForm() {
      this.$refs.addressFormSectionRef?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    },

    loadLeafletAssets() {
      if (window.L) {
        this.leafletReady = true;
        return Promise.resolve(window.L);
      }

      if (this.leafletLoadPromise) {
        return this.leafletLoadPromise;
      }

      this.leafletLoadPromise = new Promise((resolve, reject) => {
        if (!document.getElementById("leaflet-css")) {
          const link = document.createElement("link");
          link.id = "leaflet-css";
          link.rel = "stylesheet";
          link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
          document.head.appendChild(link);
        }

        const existingScript = document.querySelector(
          'script[data-leaflet-loader="true"]'
        );

        const handleReady = () => {
          this.leafletReady = true;
          resolve(window.L);
        };

        if (existingScript) {
          existingScript.addEventListener("load", handleReady, { once: true });
          existingScript.addEventListener(
            "error",
            () => reject(new Error("Failed to load Leaflet")),
            { once: true }
          );
          return;
        }

        const script = document.createElement("script");
        script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
        script.async = true;
        script.dataset.leafletLoader = "true";
        script.onload = handleReady;
        script.onerror = () =>
          reject(new Error("Failed to load Leaflet library"));
        document.body.appendChild(script);
      });

      return this.leafletLoadPromise;
    },

    async ensureAddressMapReady() {
      try {
        const L = await this.loadLeafletAssets();
        if (this.addressMap || !this.$refs.addressMap) return;

        const latitude = Number(this.addressForm.latitude) || 10.155;
        const longitude = Number(this.addressForm.longitude) || 76.393;

        this.addressMap = L.map(this.$refs.addressMap, {
          zoomControl: true,
          attributionControl: true,
        }).setView([latitude, longitude], 16);

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19,
          attribution: "&copy; OpenStreetMap contributors",
        }).addTo(this.addressMap);

        this.addressMap.on("click", (event) => {
          this.handleAddressMapSelection(
            event.latlng.lat,
            event.latlng.lng,
            true
          );
        });

        this.syncAddressMapPosition();

        this.$nextTick(() => {
          this.addressMap?.invalidateSize();
        });
      } catch (error) {
        console.error("Map initialization failed:", error);
        this.showSnackbar("Unable to load map right now.", "error");
      }
    },

    syncAddressMapPosition() {
      if (!this.addressMap || !window.L) return;

      const latitude = Number(this.addressForm.latitude);
      const longitude = Number(this.addressForm.longitude);

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

      const latLng = [latitude, longitude];

      if (!this.addressMapMarker) {
        this.addressMapMarker = window.L.marker(latLng, {
          draggable: true,
        }).addTo(this.addressMap);

        this.addressMapMarker.on("dragend", (event) => {
          const pos = event.target.getLatLng();
          this.handleAddressMapSelection(pos.lat, pos.lng, true);
        });
      } else {
        this.addressMapMarker.setLatLng(latLng);
      }

      this.addressMap.setView(latLng, 16);
      this.$nextTick(() => {
        this.addressMap?.invalidateSize();
      });
    },

    async reverseGeocodeCoordinates(latitude, longitude) {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
      );
      return response.json();
    },

    async handleAddressMapSelection(latitude, longitude, showFeedback = false) {
      this.addressForm.latitude = latitude;
      this.addressForm.longitude = longitude;
      this.locationSearchResults = [];

      try {
        const data = await this.reverseGeocodeCoordinates(latitude, longitude);
        const addr = data?.address || {};

        this.addressForm.address_line_1 = this.buildAddressFromParts(
          addr,
          data?.display_name || this.addressForm.address_line_1
        );
        this.addressForm.city =
          this.getCityFromAddress(addr) || this.addressForm.city;
        this.addressForm.landmark =
          this.getLandmarkFromAddress(addr) || this.addressForm.landmark;
        this.locationSearchQuery = data?.display_name || this.locationSearchQuery;

        if (showFeedback) {
          this.showSnackbar("Location updated from map", "success");
        }
      } catch (error) {
        console.error("Reverse geocoding error:", error);
        if (showFeedback) {
          this.showSnackbar(
            "Location updated, but address details could not be fetched.",
            "warning"
          );
        }
      }
    },

    seedAddressCoordinates() {
      const location = this.getLocationFromStorage();
      if (!location) return;

      this.addressForm.latitude = location.latitude;
      this.addressForm.longitude = location.longitude;
    },

    async searchLocations() {
      const query = this.locationSearchQuery.trim();
      if (!query) {
        this.showSnackbar("Enter a location to search", "warning");
        return;
      }

      this.searchingLocations = true;

      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&addressdetails=1&q=${encodeURIComponent(
            query
          )}`
        );
        const data = await response.json();

        this.locationSearchResults = Array.isArray(data) ? data : [];

        if (!this.locationSearchResults.length) {
          this.showSnackbar("No matching locations found", "warning");
        }
      } catch (error) {
        console.error("Location search error:", error);
        this.showSnackbar(
          "Could not search locations right now. Please try again.",
          "error"
        );
      } finally {
        this.searchingLocations = false;
      }
    },

    applyLocationResult(result) {
      if (!result) return;

      const latitude = Number(result.lat);
      const longitude = Number(result.lon);
      const addr = result.address || {};

      this.addressForm.latitude = latitude;
      this.addressForm.longitude = longitude;
      this.addressForm.address_line_1 = this.buildAddressFromParts(
        addr,
        result.display_name
      );
      this.addressForm.city =
        this.getCityFromAddress(addr) || this.addressForm.city;
      this.addressForm.landmark =
        this.getLandmarkFromAddress(addr) || this.addressForm.landmark;
      this.locationSearchQuery = result.display_name;
      this.locationSearchResults = [];
      this.syncAddressMapPosition();

      this.showSnackbar("Location selected from map search", "success");
    },

    async detectCurrentLocation() {
      if (!navigator.geolocation) {
        this.locationHelpTone = "error";
        this.locationHelpMessage =
          "Current location is not available here. You can still select the location from the map.";
        this.showSnackbar(
          "Geolocation is not supported by your browser",
          "error"
        );
        return;
      }

      this.detectingLocation = true;
      this.locationHelpMessage = "";

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          this.addressForm.latitude = latitude;
          this.addressForm.longitude = longitude;

          try {
            await this.handleAddressMapSelection(latitude, longitude, true);
            this.locationHelpTone = "info";
            this.locationHelpMessage =
              "Current location selected successfully.";
          } catch (error) {
            console.error("Reverse geocoding error:", error);
            this.showSnackbar(
              "Location detected, but couldn't fetch address details.",
              "warning"
            );
          } finally {
            this.detectingLocation = false;
          }
        },
        (error) => {
          this.detectingLocation = false;
          let msg = "Error detecting location";
          if (error.code === 1) {
            msg = "Location permission denied";
            this.locationHelpMessage =
              "Location permission was denied. You can still select the location from the map.";
          } else if (error.code === 2) {
            msg = "Location unavailable";
            this.locationHelpMessage =
              "Current location is unavailable. You can still select the location from the map.";
          } else if (error.code === 3) {
            msg = "Location request timed out";
            this.locationHelpMessage =
              "Location request timed out. You can still select the location from the map.";
          } else {
            this.locationHelpMessage =
              "Couldn't get your current location. You can still select the location from the map.";
          }
          this.locationHelpTone = "error";

          this.showSnackbar(msg, "error");
        },
        { timeout: 10000 }
      );
    },

    resetAddressForm() {
      this.addressForm = {
        name: "",
        title: "Other",
        landmark: "",
        address_line_1: "",
        city: "",
        latitude: null,
        longitude: null,
        contact_number: "",
        is_selected: true,
      };
      this.addressFormValid = false;
      this.locationHelpMessage = "";
      this.locationHelpTone = "info";
      this.locationSearchQuery = "";
      this.locationSearchResults = [];
    },

    async saveAddress() {
      // Validate form
      if (!this.$refs.addressForm.validate()) return;

      let location = null;
      const formLatitude = Number(this.addressForm.latitude);
      const formLongitude = Number(this.addressForm.longitude);

      if (Number.isFinite(formLatitude) && Number.isFinite(formLongitude)) {
        location = {
          latitude: formLatitude,
          longitude: formLongitude,
        };
      } else {
        location = this.getLocationFromStorage();
      }

      if (!location) {
        this.showSnackbar(
          "Location not found. Please search a place or enable location.",
          "error"
        );
        return;
      }

      // Prepare payload
      const payload = {
        ...this.addressForm,
        is_selected: this.isEditMode ? this.addressForm.is_selected : true,
        latitude: location.latitude,
        longitude: location.longitude,
      };

      this.savingAddress = true;

      try {
        let res;

        /* ---------------------------------- */
        /* ADD or EDIT ADDRESS API CALL       */
        /* ---------------------------------- */
        if (this.isEditMode) {
          res = await editAddress(this.editingAddressId, payload);
        } else {
          res = await addAddress(payload);
        }

        /* ---------------------------------- */
        /* 🔥 HANDLE BUSINESS ERROR RESPONSE  */
        /* (API returns 200 but error_code=1) */
        /* ---------------------------------- */
        if (res?.data?.error_code === 1) {
          this.showSnackbar(
            res.data.message || "Address already exists. Please edit instead.",
            "error"
          );
          return; // stop execution
        }

        /* ---------------------------------- */
        /* ✅ SUCCESS                         */
        /* ---------------------------------- */
        this.showSnackbar(
          this.isEditMode
            ? "Address updated successfully"
            : "Address added successfully",
          "success"
        );

        // Refresh address list
        await this.fetchAddresses();

        // Refresh pricing/service metadata for new location
        await this.fetchServiceSelection();

        // Close form
        this.collapseAddressForm();
      } catch (error) {
        console.error("Save address failed:", error);

        this.showSnackbar(
          "Something went wrong while saving the address. Please try again.",
          "error"
        );
      } finally {
        this.savingAddress = false;
      }
    },

    openCouponDialog() {
      console.log("Opening coupon dialog");
    },

    getLocationFromStorage() {
      const raw =
        localStorage.getItem("userLocation") ||
        sessionStorage.getItem("userLocation");

      if (!raw) {
        console.error("userLocation not found in storage");
        return null;
      }

      const parsed = JSON.parse(raw);

      if (!parsed.latitude || !parsed.longitude) {
        console.error("Invalid latitude/longitude", parsed);
        return null;
      }

      return {
        latitude: parsed.latitude,
        longitude: parsed.longitude,
      };
    },

    async confirmBooking() {
      if (this.isConfirmDisabled) return;

      this.confirmLoading = true;

      try {
        // 1. Prepare Date
        let slotDate = "";
        const tzNow = this.getTimeZoneParts();
        if (this.selectedDate === "Today") {
          slotDate = this.formatDateFromParts(tzNow);
        } else if (this.selectedDate === "Tomorrow") {
          const base = new Date(
            Date.UTC(tzNow.year, tzNow.month - 1, tzNow.day)
          );
          base.setUTCDate(base.getUTCDate() + 1);
          const t = this.getTimeZoneParts(base);
          slotDate = this.formatDateFromParts(t);
        } else {
          slotDate = this.customDate;
        }
        // 2. Call Cart Add API
        const cartRes = await addToCart({
          serviceTypeId: String(this.selectedServiceTypeId),
          requested_date: slotDate,
          requested_time: this.formatSelectedTime24h(this.selectedTime),
          service_partner_id: null,
          notes: this.bookingDescription || "",
          payment_method: "cash",
        });

        if (cartRes.data?.error_code !== 0) {
          this.showSnackbar(
            cartRes.data?.message ||
              "Could not add to cart. Please check your selection.",
            "error"
          );
          this.confirmLoading = false;
          return;
        }

        // 3. Call Booking Create API
        const bookingRes = await createBooking({
          description: this.bookingDescription || "No specific instructions",
          platform: "Web",
        });

        if (bookingRes.data?.error_code !== 0) {
          this.showSnackbar(
            bookingRes.data?.message || "Booking failed. Please try again.",
            "error"
          );
          this.confirmLoading = false;
          return;
        }

        // 4. Success handling
        sessionStorage.removeItem("selectedCategory");
        sessionStorage.removeItem("selectedService");

        this.confirmLoading = false;
        this.bookingSuccessDialog = true;
        this.$nextTick(() => {
          this.animateSuccess();
        });
      } catch (error) {
        console.error("Booking process error:", error);
        this.confirmLoading = false;
        this.showSnackbar(
          error.response?.data?.message ||
            "Something went wrong during booking. Please try again.",
          "error"
        );
      }
    },

    goToBookings() {
      this.$router.push("/bookings");
    },

    goToHome() {
      this.$router.push("/");
    },
    animateSuccess() {
      gsap.from(".success-icon-wrapper", {
        scale: 0,
        duration: 0.8,
        ease: "back.out(1.7)",
      });
      gsap.from(".success-title", {
        y: 20,
        opacity: 0,
        duration: 0.5,
        delay: 0.3,
      });
      gsap.from(".success-subtitle", {
        y: 20,
        opacity: 0,
        duration: 0.5,
        delay: 0.4,
      });
    },
  },
};
</script>

<style scoped>
/* ============================================ */
/* BASE STYLES */
/* ============================================ */
.booking-page {
  background: #f5f5f5;
  min-height: 100vh;
  padding-bottom: 40px;
  overflow-x: hidden;
}

.booking-container {
  width: 100%;
  max-width: 100%;
}

/* ============================================ */
/* PAGE HEADER */
/* ============================================ */
.page-header {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.page-header-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fff3ed;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 4px 0;
  line-height: 1.2;
}

.service-name {
  font-size: 16px;
  color: #666;
  margin: 0;
  font-weight: 500;
}

/* ============================================ */
/* BOOKING CARD */
/* ============================================ */
.booking-card {
  border-radius: 16px;
  padding: 0;
  overflow: hidden;
  box-shadow: none !important;
  border: 1px solid #f0f0f0;
}

.booking-top-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0;
  background: #f3f4f6;
}

.booking-section {
  padding: 24px;
}

.booking-top-card {
  background: #fff;
}

.booking-top-card:first-child {
  border-right: 1px solid #ededed;
}

.section-divider {
  margin: 0;
  border-color: #f3f3f3 !important;
}

.booking-section-simple {
  background: #fff;
}

.booking-top-layout {
  display: grid;
  grid-template-columns: minmax(280px, 0.9fr) minmax(360px, 1.4fr);
  gap: 20px;
  align-items: stretch;
  padding: 20px;
  background: #f7f7f7;
}

.booking-time-card {
  border: 1px solid #ececec;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  display: flex;
  flex-direction: column;
  height: 100%;
}

.booking-address-card {
  min-height: 100%;
  border: 1px solid #ececec;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  display: flex;
  flex-direction: column;
  height: 100%;
}

.simple-section-header {
  margin-bottom: 14px;
}

.simple-section-title {
  font-size: 18px;
  font-weight: 700;
  color: #232323;
  margin: 0 0 4px;
  line-height: 1.35;
}

.simple-section-description {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #b0b0b0;
}

/* ============================================ */
/* STEP HEADER */
/* ============================================ */
.step-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f46523 0%, #ff8855 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(244, 101, 35, 0.3);
}

.step-content {
  flex: 1;
}

.step-title {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 4px 0;
  line-height: 1.3;
}

.step-description {
  font-size: 15px;
  color: #666;
  margin: 0;
}
.step-optional {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #fff3ed;
  color: #f46523;
  font-weight: 600;
  font-size: 12px;
}

.step-header.step-completed .step-number {
  background: #4caf50;
  box-shadow: 0 2px 6px rgba(76, 175, 80, 0.3);
}

.step-header.step-disabled {
  opacity: 0.6;
  filter: grayscale(0.5);
  pointer-events: none;
}

.step-header.step-disabled .step-number {
  background: #e0e0e0;
  color: #999;
  box-shadow: none;
}

/* ============================================ */
/* SELECTION SUMMARIES */
/* ============================================ */
.selection-summary-pill {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fffaf7;
  border: 1px solid #f6e8dd;
  padding: 10px 12px;
  border-radius: 8px;
  flex-wrap: wrap;
}

.summary-icon {
  width: 20px;
  height: 20px;
  background: #f46523;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.summary-text {
  font-size: 15px;
  color: #444;
}

.summary-text strong {
  color: #f46523;
}

.summary-separator {
  margin: 0 6px;
  color: #c7a08b;
}

/* ============================================ */
/* DATE & TIME SELECTION - COMPACT */
/* ============================================ */
.datetime-compact {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}

.booking-time-card .datetime-compact {
  flex: 1;
}

.booking-time-card .compact-group {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.booking-time-card .compact-buttons {
  margin-top: 2px;
}

.booking-time-card .selection-summary-pill {
  margin-top: auto !important;
}

.compact-group {
  width: 100%;
}

.compact-label {
  font-size: 13px;
  font-weight: 600;
  color: #555;
  margin-bottom: 8px;
  display: block;
}

.compact-buttons {
  display: flex;
  gap: 8px;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
}

.compact-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  border-radius: 999px !important;
  font-size: 12px !important;
  height: 34px !important;
  min-width: 0 !important;
  padding: 0 10px !important;
  transition: all 0.2s;
  width: 100% !important;
  max-width: 100%;
  white-space: nowrap !important;
}

.compact-btn.active-compact {
  font-weight: 600 !important;
  box-shadow: 0 2px 4px rgba(244, 101, 35, 0.2);
}

/* Time Sheet Styles */
.time-select-btn {
  height: 44px !important;
  border-radius: 999px !important;
  border: 1px solid #f2b190 !important;
  text-transform: none !important;
  justify-content: flex-start !important;
  padding: 0 12px !important;
  font-weight: 500 !important;
  color: #f46523 !important;
  background: white !important;
  box-shadow: none !important;
}

.time-select-btn-inline {
  min-width: 0 !important;
  margin-left: 0 !important;
}

.time-sheet-card {
  border-radius: 16px !important;
  max-height: 80vh;
}

.sheet-header {
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
}

.sheet-title {
  font-size: 17px;
  font-weight: 700;
  color: #1a1a1a;
}

.sheet-content {
  padding: 16px !important;
}

/* Time List Grid */
.time-list-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 12px;
  padding: 4px;
}

.time-slot-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 0;
  background: #f5f5f5;
  border-radius: 8px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #333;
  transition: all 0.2s;
  border: 1px solid transparent;
}

.time-slot-btn:hover:not(.disabled) {
  background: #fff3ed;
  border-color: #f46523;
  color: #f46523;
}

.time-slot-btn.selected {
  background: #f46523;
  color: white;
  border-color: #f46523;
  box-shadow: 0 4px 8px rgba(244, 101, 35, 0.3);
}

.time-slot-btn.disabled {
  opacity: 0.5;
  background: #eee;
  color: #bbb;
  cursor: not-allowed;
  pointer-events: none;
}

/* ============================================ */
/* PARTNER SECTION */
/* ============================================ */
.partner-selected {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  background: #fff3ed;
  border-radius: 8px;
  border: 1px solid #f46523;
}

.partner-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.partner-name {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 2px;
}

.partner-meta {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #666;
  flex-wrap: wrap;
  gap: 6px;
}

.partner-jobs {
  font-size: 14px;
  color: #666;
}

.select-partner-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  height: 40px !important;
  border-radius: 8px !important;
  font-size: 14px !important;
}

.address-selected {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px;
  background: #fff7f2;
  border-radius: 12px;
  border: 1px solid #ffd9c8;
  margin-bottom: 14px;
}

.address-selected-info {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.address-selected-copy {
  min-width: 0;
}

.address-selected-title {
  font-size: 15px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 2px;
}

.address-selected-line {
  font-size: 14px;
  color: #444;
  line-height: 1.5;
  word-break: break-word;
}

.address-selected-meta {
  margin-top: 4px;
  font-size: 13px;
  color: #777;
}

.select-address-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  min-height: 42px !important;
  border-radius: 10px !important;
  font-size: 14px !important;
  margin-bottom: 14px;
  color: #f46523 !important;
  border: 1px solid #ffd6c2 !important;
  box-shadow: none !important;
}

.select-address-btn-active {
  color: #ffffff !important;
  border-color: #f46523 !important;
  box-shadow: 0 6px 16px rgba(244, 101, 35, 0.22) !important;
}

.address-list-wrap {
  margin-top: 8px;
}

.address-simple-block {
  display: grid;
  gap: 14px;
}

.address-selected-simple {
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  background: #fff;
  padding: 14px;
}

.address-selected-simple__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.address-selected-simple__title-wrap {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
}

.address-selected-simple__title {
  font-size: 15px;
  font-weight: 700;
  color: #1f1f1f;
  line-height: 1.3;
}

.address-selected-simple__phone {
  margin-top: 2px;
  font-size: 13px;
  color: #666;
}

.address-selected-simple__line {
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  word-break: break-word;
}

.address-selected-simple__meta {
  margin-top: 6px;
  font-size: 13px;
  color: #000;
}

.address-simple-actions {
  display: grid;
  gap: 12px;
}

.address-simple-btn {
  height: 42px !important;
  border-radius: 4px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 700 !important;
  box-shadow: none !important;
}

.address-modal-card,
.address-form-modal {
  border-radius: 18px !important;
  overflow: hidden;
}

.address-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px 14px;
  background: #fffaf6;
  border-bottom: 1px solid #f4e6de;
}

.address-modal-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #1f1f1f;
}

.address-modal-subtitle {
  margin: 6px 0 0;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
}

.address-modal-body {
  max-height: 65vh;
  overflow-y: auto;
  padding: 14px 16px 6px;
}

.address-modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 14px 16px 16px;
  border-top: 1px solid #efefef;
  background: #fff;
}

/* ============================================ */
/* SELECTION PANELS (Partner & Address Forms) */
/* ============================================ */
.selection-panel {
  border-radius: 8px;
  border: 2px solid #e0e0e0;
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fafafa;
}

.panel-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
}

.panel-content {
  padding: 16px;
  max-height: 400px;
  overflow-y: auto;
}

.panel-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 16px;
  background: #fafafa;
  border-top: 1px solid #e0e0e0;
}

/* ============================================ */
/* PARTNER LIST */
/* ============================================ */
.partner-list {
  margin-top: 0;
}

.partner-card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  border: 1.5px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.partner-card:hover {
  border-color: #f46523;
  background: #fffbf8;
}

.partner-card.partner-selected-card {
  border-color: #f46523;
  background: #fff3ed;
  box-shadow: 0 2px 6px rgba(244, 101, 35, 0.1);
}

.partner-radio {
  margin-right: 10px;
  margin-top: 2px;
}

.partner-details {
  flex: 1;
  min-width: 0;
}

.partner-name-row {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  gap: 8px;
  min-width: 0;
  flex-wrap: wrap;
}

.partner-title {
  font-size: 16px;
  color: #1a1a1a;
  word-break: break-word;
}

.partner-info-row {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #666;
  flex-wrap: wrap;
  gap: 6px;
}

.info-item {
  display: flex;
  align-items: center;
}

.info-divider {
  color: #ccc;
}

/* ============================================ */
/* COMPACT ADDRESS SECTION */
/* ============================================ */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  text-align: center;
  padding: 30px 20px;
}

.loading-text {
  margin-top: 12px;
  color: #666;
  font-size: 13px;
}

.loading-state-compact {
  min-height: 220px;
}

/* Compact Address Card Design */
.address-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  margin-bottom: 8px;
  overflow: hidden;
  transition: all 0.2s;
}

.address-card:hover {
  border-color: #f46523;
  box-shadow: 0 2px 8px rgba(244, 101, 35, 0.1);
}

.address-card.address-selected-card {
  border-color: #f46523;
  background: linear-gradient(135deg, #fff9f5 0%, #fff3ed 100%);
  box-shadow: 0 4px 12px rgba(244, 101, 35, 0.1);
  position: relative;
}

.address-card.address-selected-card::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(to bottom, #f46523, #ff8855);
}

.address-card-content {
  padding: 10px 12px;
  cursor: pointer;
  overflow: hidden;
}

/* Address Header - Compact */
.address-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 6px;
  gap: 10px;
}

.address-type-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.address-type-icon {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}

.address-info-compact {
  flex: 1;
  min-width: 0;
}

.address-type-text {
  font-size: 14px;
  font-weight: 700;
  color: #222;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  display: block;
  margin-bottom: 3px;
}

.address-contact-compact {
  font-size: 14px;
  color: #4b5563;
  font-weight: 600;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.address-radio {
  margin: 0;
  padding: 0;
}

/* Address Details - Compact */
.address-details-compact {
  margin-bottom: 6px;
  padding-left: 2px;
}

.address-location-line {
  display: flex;
  align-items: flex-start;
  margin-bottom: 3px;
  gap: 4px;
}

.address-text-compact {
  font-size: 15px;
  color: #374151;
  line-height: 1.45;
  flex: 1;
  min-width: 0;
  word-break: break-word;
}

.address-meta-line {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #6b7280;
  padding-left: 18px;
  flex-wrap: wrap;
}

.address-landmark {
  font-style: italic;
}

.address-city {
  font-weight: 500;
}

/* Address Actions - Compact */
.address-actions-compact {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.action-btn-compact {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  letter-spacing: 0.3px !important;
  min-width: 72px !important;
  padding: 0 10px !important;
  height: 28px !important;
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 6px !important;
  transition: all 0.2s ease;
  border: 1px solid #f46523 !important;
  background: #f46523 !important;
  color: #fff !important;
}

.action-btn-compact.edit-btn:hover {
  background-color: #df591a !important;
  border-color: #df591a !important;
}

.action-btn-compact.delete-btn:hover {
  background-color: #df591a !important;
  border-color: #df591a !important;
}

.action-text {
  font-size: 12px;
  color: #fff;
}

/* Delete Confirmation Section */
.delete-confirmation-section {
  padding: 16px;
  background: #fff5f5;
  border-left: 3px solid #f44336;
}

.delete-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.delete-title {
  font-size: 14px;
  font-weight: 600;
  color: #d32f2f;
}

.delete-message {
  font-size: 13px;
  color: #666;
  line-height: 1.4;
  margin-bottom: 12px;
}

.delete-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.cancel-delete-btn {
  text-transform: none !important;
  font-weight: 500 !important;
  font-size: 12px !important;
  height: 28px !important;
}

.confirm-delete-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  height: 28px !important;
  border-radius: 4px !important;
  background: linear-gradient(135deg, #f44336 0%, #ef5350 100%) !important;
}

/* Add Address Button - Compact */
.add-address-btn-compact {
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  height: 36px !important;
  border: 1px dashed #f46523 !important;
  color: #f46523 !important;
  font-size: 13px !important;
}

.add-address-btn-compact:hover {
  background: #fff3ed !important;
}

/* No Address State - Compact */
.no-address-state-compact {
  text-align: center;
  padding: 30px 16px;
  background: white;
  border: 1px dashed #e0e0e0;
  border-radius: 10px;
}

.no-address-content {
  max-width: 280px;
  margin: 0 auto;
}

.no-address-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
}

.no-address-text {
  font-size: 13px;
  color: #666;
  margin-bottom: 16px;
}

.add-address-primary-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  height: 36px !important;
  font-size: 13px !important;
}

/* Address Form Panel - Compact */
.address-form-panel {
  border-radius: 10px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}

.address-panel-content {
  padding: 20px !important;
}

.address-form-layout {
  display: grid;
  grid-template-columns: minmax(320px, 440px) minmax(360px, 1fr);
  gap: 24px;
  align-items: start;
}

.address-form-fields {
  min-width: 0;
}

.address-location-tools {
  margin-bottom: 10px;
}

.map-search-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.map-search-input {
  flex: 1;
}

.map-search-btn {
  margin-top: 2px;
  min-width: 92px !important;
  height: 40px !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
}

.map-picker-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 6px;
  margin-bottom: 12px;
}

.location-help-message {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-bottom: 12px;
  font-size: 12px;
  line-height: 1.5;
  color: #5f6b7a;
}

.location-help-message-error {
  color: #c05621;
}

.map-open-link {
  font-size: 13px;
  font-weight: 600;
  color: #f46523;
  text-decoration: none;
}

.map-search-results {
  display: grid;
  gap: 8px;
  margin-bottom: 14px;
}

.map-result-item {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #f1dfd5;
  border-radius: 10px;
  background: #fff;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 4px;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.map-result-item:hover {
  border-color: #f46523;
  background: #fff7f2;
}

.map-result-name {
  font-size: 13px;
  line-height: 1.45;
  color: #333;
}

.map-result-coords {
  font-size: 12px;
  color: #7b7b7b;
}

.address-map-pane {
  min-height: 100%;
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  background: #f7f7f7;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.04);
}

.map-pane-toolbar {
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 500;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  max-width: 260px;
}

.map-pane-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f1f1f;
}

.map-pane-note {
  margin-top: 3px;
  font-size: 12px;
  line-height: 1.4;
  color: #666;
}

.address-map-frame {
  width: 100%;
  min-height: 620px;
  border: 0;
  background: #f5f5f5;
}

.address-map-loading {
  opacity: 0.6;
}

.map-coordinates {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  position: absolute;
  left: 14px;
  bottom: 14px;
  z-index: 500;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(255, 243, 237, 0.96);
  color: #f46523;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 6px 18px rgba(244, 101, 35, 0.12);
}

.map-preview-empty {
  position: absolute;
  inset: 0;
  padding: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7a685f;
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
  background: rgba(255, 255, 255, 0.88);
  z-index: 400;
}

.form-field-compact >>> .v-label {
  font-size: 13px !important;
}

.form-field-compact >>> .v-input__control {
  min-height: 40px !important;
}

/* Compact Address Type Selector */
.address-type-selector-compact {
  margin-bottom: 12px;
}

.address-type-label {
  font-size: 12px;
  font-weight: 600;
  color: #555;
  margin-bottom: 8px;
}

.address-type-options {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.address-type-option-compact {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1.5px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
  flex: 1;
  min-width: 80px;
  justify-content: center;
}

.address-type-option-compact:hover {
  border-color: #f46523;
  background: #fffbf8;
}

.address-type-option-compact.address-type-selected {
  border-color: #f46523;
  background: #fff3ed;
  box-shadow: 0 1px 4px rgba(244, 101, 35, 0.1);
}

.type-option-icon-compact {
  width: 22px;
  height: 22px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.type-option-text {
  font-size: 13px;
  font-weight: 500;
  color: #333;
}

.default-address-checkbox >>> .v-label {
  font-size: 13px !important;
  color: #555 !important;
}

/* Compact Panel Footer */
.panel-footer-compact {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  background: #fafafa;
  border-top: 1px solid #e0e0e0;
}

.cancel-btn {
  text-transform: none !important;
  font-weight: 500 !important;
  color: #666 !important;
  font-size: 13px !important;
  height: 32px !important;
}

.save-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 6px !important;
  padding: 0 16px !important;
  font-size: 13px !important;
  height: 32px !important;
}

/* ============================================ */
/* PAYMENT SECTION */
/* ============================================ */
.payment-sticky {
  position: sticky;
  top: 20px;
}

.coupon-card {
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #e0e0e0;
  margin-bottom: 12px;
}

.coupon-card:hover {
  border-color: #f46523;
  box-shadow: 0 3px 8px rgba(244, 101, 35, 0.1);
}

.coupon-content {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px;
}

.coupon-text {
  flex: 1;
}

.coupon-title {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 2px;
}

.coupon-subtitle {
  font-size: 12px;
  color: #666;
}

.payment-card {
  border-radius: 12px;
  padding: 22px;
  box-shadow: none !important;
  border: 1px solid #f0f0f0;
  background: #fbfbfb !important;
}

.payment-header {
  margin-bottom: 6px;
}

.payment-title {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
}

.payment-content {
  padding-top: 6px;
}

.payment-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.payment-label {
  font-size: 13px;
  color: #555;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.payment-value {
  font-size: 14px;
  color: #1a1a1a;
  font-weight: 600;
}

.payment-value.discount {
  color: #4caf50;
  font-size: 13px;
}

.discount-row .payment-label {
  color: #4caf50;
  font-size: 13px;
}

.payment-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-top: 1px solid #e0e0e0;
  margin-top: 8px;
}

.total-label {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
}

.total-amount {
  font-size: 20px;
  font-weight: 700;
  color: #f46523;
}

.policy-box {
  background: #fff3ed;
  border-left: 3px solid #f46523;
  padding: 12px;
  border-radius: 6px;
  margin: 16px 0;
}

.policy-header {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.policy-title {
  font-size: 12px;
  font-weight: 700;
  color: #f46523;
}

.policy-text {
  font-size: 12px;
  color: #555;
  line-height: 1.5;
  margin: 0;
}

.confirm-btn {
  text-transform: none !important;
  font-weight: 700 !important;
  font-size: 15px !important;
  letter-spacing: 0.3px !important;
  border-radius: 8px !important;
  height: 48px !important;
  box-shadow: 0 3px 8px rgba(244, 101, 35, 0.2) !important;
  margin-top: 16px;
}

.confirm-btn:hover {
  box-shadow: 0 4px 12px rgba(244, 101, 35, 0.3) !important;
}

.confirm-btn:disabled {
  background: #ccc !important;
  box-shadow: none !important;
}

.booking-note {
  text-align: center;
  font-size: 11px;
  color: #999;
  margin: 10px 0 0 0;
}

.missing-steps-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #777;
  background: #f9f9f9;
  padding: 8px;
  border-radius: 6px;
}

.auto-assign-note {
  border-radius: 8px !important;
  font-size: 13px !important;
}

/* ============================================ */
/* RESPONSIVE */
/* ============================================ */
@media (max-width: 960px) {
  .booking-top-layout {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 16px;
  }

  .booking-time-card {
    border-right: 1px solid #ececec;
    border-bottom: 1px solid #ececec;
  }

  .booking-top-grid {
    grid-template-columns: 1fr;
  }

  .booking-top-card:first-child {
    border-right: 0;
    border-bottom: 1px solid #ededed;
  }

  .datetime-compact {
    flex-direction: column;
    gap: 16px;
  }

  .payment-sticky {
    position: static;
  }
}

@media (max-width: 600px) {
  .booking-page {
    padding-bottom: 24px;
  }

  .booking-container {
    padding: 12px !important;
  }

  .page-header {
    padding: 14px;
    border-radius: 10px;
  }

  .page-header-row {
    align-items: flex-start;
  }

  .page-title {
    font-size: 22px;
  }

  .booking-section {
    padding: 18px 16px;
  }

  .step-header {
    gap: 12px;
    margin-bottom: 20px;
  }

  .step-number {
    width: 28px;
    height: 28px;
    font-size: 13px;
  }

  .step-title {
    font-size: 18px;
  }

  .step-description {
    font-size: 13px;
    line-height: 1.5;
  }

  .datetime-compact {
    gap: 16px;
  }

  .booking-time-card .compact-group {
    gap: 8px;
  }

  .compact-buttons {
    gap: 8px;
  }

  .compact-btn {
    font-size: 13px !important;
    height: 40px !important;
    min-width: 65px !important;
    padding: 0 14px !important;
    border-width: 1.5px !important;
  }

  .booking-time-card .compact-label {
    margin-bottom: 10px;
  }

  .booking-time-card .selection-summary-pill {
    padding: 12px 14px;
  }

  .selection-summary-pill {
    padding: 12px 14px;
    margin-top: 18px !important;
  }

  .summary-text {
    font-size: 14px;
    line-height: 1.5;
  }

  .partner-selected {
    align-items: flex-start;
    flex-wrap: wrap;
    padding: 14px;
  }

  .partner-card {
    padding: 14px 12px;
    align-items: flex-start;
    margin-bottom: 12px;
  }

  .partner-radio {
    margin-right: 0;
  }

  .partner-details {
    width: 100%;
  }

  .partner-name-row {
    align-items: flex-start;
  }

  .partner-info-row {
    gap: 4px;
  }

  .info-divider {
    display: none;
  }

  .address-card-content,
  .delete-confirmation-section {
    padding: 14px 12px;
  }

  .address-modal-header {
    padding: 16px;
  }

  .address-modal-title {
    font-size: 18px;
  }

  .address-modal-subtitle {
    font-size: 13px;
  }

  .address-modal-body {
    padding: 14px 14px 8px;
  }

  .address-modal-footer {
    padding: 14px 16px 16px;
    flex-direction: column-reverse;
  }

  .address-modal-footer .v-btn {
    width: 100%;
  }

  .address-panel-content {
    padding: 18px !important;
  }

  .address-form-layout {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .map-picker-actions {
    align-items: flex-start;
    flex-direction: column;
  }

  .address-map-frame {
    min-height: 320px;
  }

  .map-preview-empty {
    min-height: 320px;
  }

  .address-header {
    align-items: flex-start;
    margin-bottom: 10px;
  }

  .address-type-icon {
    width: 24px;
    height: 24px;
  }

  .address-type-text {
    font-size: 12px;
  }

  .address-contact-compact {
    font-size: 12px;
  }

  .address-text-compact {
    font-size: 13px;
  }

  .address-meta-line {
    font-size: 12px;
    padding-left: 0;
    row-gap: 4px;
  }

  .action-text {
    display: inline;
    font-size: 12px;
  }

  .address-actions-compact {
    justify-content: stretch;
    gap: 10px;
    padding-top: 12px;
    margin-top: 10px;
  }

  .action-btn-compact {
    flex: 0 1 auto;
    min-width: 88px !important;
    padding: 0 8px !important;
    height: 30px !important;
  }

  .delete-title {
    font-size: 13px;
  }

  .delete-message {
    font-size: 12px;
  }

  .payment-card {
    padding: 16px;
  }

  .payment-title {
    font-size: 17px;
  }

  .payment-label {
    font-size: 13px;
  }

  .payment-value {
    font-size: 14px;
  }

  .total-label {
    font-size: 16px;
  }

  .total-amount {
    font-size: 19px;
  }

  .confirm-btn {
    height: 44px !important;
    font-size: 14px !important;
  }
}

@media (max-width: 400px) {
  .booking-container {
    padding: 10px !important;
  }

  .page-header {
    padding: 12px;
  }

  .booking-section {
    padding: 16px 14px;
  }

  .compact-buttons {
    gap: 8px;
  }

  .compact-btn {
    height: 38px !important;
    font-size: 12px !important;
    padding: 0 12px !important;
  }

  .step-header {
    align-items: flex-start;
  }

  .address-panel-content {
    padding: 12px !important;
  }

  .map-search-row {
    flex-direction: column;
  }

  .map-search-btn {
    width: 100%;
    margin-top: 0;
  }

  .address-map-frame {
    min-height: 260px;
  }

  .map-preview-empty {
    min-height: 260px;
  }

  .step-optional {
    display: block;
    margin-left: 0;
    margin-top: 6px;
    width: fit-content;
  }

  .partner-card {
    flex-wrap: wrap;
  }

  .partner-radio {
    order: 2;
    margin-left: auto;
  }

  .partner-details {
    order: 3;
  }

  .address-header {
    flex-wrap: wrap;
  }

  .address-radio {
    margin-left: auto;
  }

  .address-actions-compact {
    flex-direction: row;
    justify-content: flex-end;
  }

  .address-selected {
    align-items: flex-start;
    flex-wrap: wrap;
    padding: 12px;
  }

  .address-selected-simple {
    padding: 14px;
  }

  .address-selected-simple__header {
    align-items: flex-start;
  }

  .select-address-btn {
    margin-bottom: 12px;
  }

  .action-btn-compact {
    width: auto;
    min-width: 82px !important;
    height: 28px !important;
    padding: 0 8px !important;
  }

  .address-type-options {
    flex-direction: column;
  }

  .address-type-option-compact {
    min-width: 100%;
    justify-content: flex-start;
  }

  .delete-actions {
    flex-direction: column;
    gap: 6px;
  }

  .cancel-delete-btn,
  .confirm-delete-btn {
    width: 100%;
    justify-content: center;
  }
}

.global-snackbar {
  z-index: 9999 !important;
}

/* Confirm button disabled state → light gray */
.confirm-btn.v-btn--disabled {
  background: #e0e0e0 !important; /* light gray */
  color: #9e9e9e !important; /* soft text gray */
  box-shadow: none !important;
  cursor: not-allowed !important;
}
/* FORCE light gray disabled button */
::v-deep(.confirm-btn.v-btn--disabled) {
  background-color: #e0e0e0 !important;
  color: #9e9e9e !important;
  box-shadow: none !important;
  cursor: not-allowed !important;
  opacity: 1 !important; /* Vuetify reduces opacity by default */
}
</style>
<style scoped>
/* Selection Summary Styles */
.selection-summary {
  background: #fff3ed;
  border: 1px dashed #f46523;
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  animation: fadeIn 0.5s ease-out;
}

.summary-text {
  font-size: 14px;
  color: #333;
}

.summary-text strong {
  color: #f46523;
}

/* Modern Success Dialog Styles */
.success-dialog-modern {
  border-radius: 20px !important;
  overflow: hidden;
}

.success-header-graphic {
  background: linear-gradient(135deg, #f46523 0%, #ff8c52 100%);
  height: 124px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.success-icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  padding: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  z-index: 2;
}

.success-body {
  padding: 20px 22px 10px !important;
}

.success-title {
  font-size: 24px;
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 2px;
}

.success-booking-id {
  font-size: 13px;
  font-weight: 600;
  color: #f46523;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.success-subtitle {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin-bottom: 0;
}

.booking-details-summary {
  background: #f9f9f9;
  border-radius: 12px;
  padding: 14px;
  text-align: left;
}

.detail-item {
  display: flex;
  align-items: center;
}

.detail-label {
  font-size: 13px;
  color: #888;
  width: 70px;
  font-weight: 500;
}

.detail-value {
  font-size: 14px;
  color: #333;
  font-weight: 600;
}

.success-app-links {
  margin-top: 14px;
  text-align: left;
}

.success-app-title {
  font-size: 11px;
  font-weight: 700;
  color: #8b8b8b;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 8px;
}

.success-app-buttons {
  display: grid;
  gap: 8px;
}

.success-store-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  border-radius: 12px;
  text-decoration: none;
  font-size: 15px;
  font-weight: 700;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid #ececec;
}

.success-store-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
}

.app-store-btn {
  background: #fff;
  color: #1f1f1f;
}

.play-store-btn {
  background: #e9f6e7;
  color: #2e7d32;
  border-color: #cfe8ca;
}

.success-action-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 700 !important;
  letter-spacing: 0.5px !important;
  min-height: 44px !important;
}

.close-dialog-btn {
  text-transform: none !important;
  font-weight: 600 !important;
}

.success-actions {
  padding: 16px 22px 20px !important;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
